import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Divider,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Box,
  Alert,
  Popover,
} from "@mui/material";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import {
  useGetEventQuery,
  useAddCommentMutation,
  useDeleteCommentMutation,
  useGetEventMediaQuery,
  useUploadEventMediaMutation,
  useUpdateEventMediaVisibilityMutation,
  useDownloadEventMediaMutation,
  useDeleteEventMediaMutation,
} from "../../features/events/eventsApiSlice";
import { gradArray } from "../form/fields/inputFields";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import EventRegistrationsTab from "./EventRegistrationsTab";
import EventParticipantsOverviewTab from "./EventParticipantsOverviewTab";

const gradLabel = (degree) =>
  gradArray.find((g) => g.value === Number(degree))?.label ||
  String(degree ?? "");

const STATUS_LABELS = {
  geplant: "Geplant",
  abgesagt: "Abgesagt",
  abgeschlossen: "Abgeschlossen",
};

const toMemberId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  return String(value?._id || value?.id || "");
};

const toMemberName = (member) => {
  const lastName = member?.lastName || "";
  const firstName = member?.firstName || "";
  const fullName = [lastName, firstName].filter(Boolean).join(", ");
  if (fullName) return fullName;
  return member?.memberNumber || toMemberId(member) || "Unbekanntes Mitglied";
};

export default function EventDetailDialog({
  open,
  onClose,
  eventId,
  canManage = false,
  initialTab = 0,
}) {
  const dispatch = useDispatch();
  const {
    data: event,
    isFetching,
    refetch,
  } = useGetEventQuery(eventId, {
    skip: !eventId,
  });
  const {
    data: mediaPayload,
    isFetching: isMediaFetching,
    refetch: refetchMedia,
  } = useGetEventMediaQuery(eventId, {
    skip: !eventId,
  });
  const [addComment, { isLoading: isAddingComment }] = useAddCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();
  const [uploadEventMedia, { isLoading: isUploadingMedia }] =
    useUploadEventMediaMutation();
  const [updateEventMediaVisibility] = useUpdateEventMediaVisibilityMutation();
  const [downloadEventMedia, { isLoading: isDownloadingMedia }] =
    useDownloadEventMediaMutation();
  const [deleteEventMedia, { isLoading: isDeletingMedia }] =
    useDeleteEventMediaMutation();

  const [commentText, setCommentText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showMediaForMembers, setShowMediaForMembers] = useState(false);
  const [consentTab, setConsentTab] = useState(0);
  const [mainTab, setMainTab] = useState(initialTab);

  // Confirmation Popover for media delete
  const [deletePopoverAnchor, setDeletePopoverAnchor] = useState(null);
  const [pendingDeleteMedia, setPendingDeleteMedia] = useState(null);

  useEffect(() => {
    if (open) {
      setMainTab(initialTab);
      return;
    }

    setConsentTab(0);
    setMainTab(initialTab);
    setDeletePopoverAnchor(null);
    setPendingDeleteMedia(null);
  }, [open, eventId, initialTab]);

  const mediaItems = Array.isArray(mediaPayload?.media)
    ? mediaPayload.media
    : [];
  const attendees = useMemo(
    () => (Array.isArray(event?.hasAttendees) ? event.hasAttendees : []),
    [event?.hasAttendees],
  );
  const registeredParticipants = useMemo(
    () =>
      (Array.isArray(event?.participants) ? event.participants : [])
        .map((entry) => {
          const member = entry?.memberId;
          const memberId = toMemberId(member);
          if (!memberId) return null;
          return {
            key: `${memberId}:${entry?._id || entry?.createdAt || "entry"}`,
            memberId,
            member,
            photoOptOut: Boolean(entry?.photoOptOut),
            createdAt: entry?.createdAt,
          };
        })
        .filter(Boolean),
    [event?.participants],
  );
  const participantsWithConsent = useMemo(
    () => registeredParticipants.filter((entry) => !entry.photoOptOut),
    [registeredParticipants],
  );
  const participantsWithoutConsent = useMemo(
    () => registeredParticipants.filter((entry) => entry.photoOptOut),
    [registeredParticipants],
  );
  const attendeeOnly = useMemo(() => {
    const registeredIds = new Set(
      registeredParticipants.map((entry) => String(entry.memberId)),
    );
    return attendees.filter((member) => !registeredIds.has(toMemberId(member)));
  }, [attendees, registeredParticipants]);
  const activeConsentList =
    consentTab === 0 ? participantsWithConsent : participantsWithoutConsent;

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await addComment({ id: eventId, text: commentText.trim() }).unwrap();
      setCommentText("");
      refetch();
    } catch {
      // error handled by RTK Query
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment({ id: eventId, commentId }).unwrap();
      refetch();
    } catch {
      // error handled by RTK Query
    }
  };

  const handleMediaUpload = async () => {
    if (!selectedFiles.length) return;
    try {
      await uploadEventMedia({
        id: eventId,
        files: selectedFiles,
        isVisibleToMembers: showMediaForMembers,
      }).unwrap();
      setSelectedFiles([]);
      setShowMediaForMembers(false);
      await Promise.all([refetch(), refetchMedia()]);
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Anhänge hochgeladen",
          data: {
            successText: `${selectedFiles.length} Datei(en) wurden gespeichert.`,
          },
        }),
      );
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Upload fehlgeschlagen",
          data: {
            errorText: error?.data?.message || "Bitte erneut versuchen.",
          },
        }),
      );
    }
  };

  const handleToggleMediaVisibility = async (media) => {
    try {
      await updateEventMediaVisibility({
        id: eventId,
        mediaId: media._id,
        isVisibleToMembers: !media.isVisibleToMembers,
      }).unwrap();
      await refetchMedia();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Sichtbarkeit konnte nicht aktualisiert werden",
          data: {
            errorText: error?.data?.message || "Bitte erneut versuchen.",
          },
        }),
      );
    }
  };

  const handleDownloadMedia = async (media) => {
    try {
      const blob = await downloadEventMedia({
        id: eventId,
        mediaId: media._id,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = media.fileName || "event-media";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Download fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message || "Datei konnte nicht geladen werden.",
          },
        }),
      );
    }
  };

  const handleDeleteMediaClick = (e, media) => {
    setDeletePopoverAnchor(e.currentTarget);
    setPendingDeleteMedia(media);
  };

  const handleDeleteMediaConfirm = async () => {
    setDeletePopoverAnchor(null);
    if (!pendingDeleteMedia) return;
    try {
      await deleteEventMedia({
        id: eventId,
        mediaId: pendingDeleteMedia._id,
      }).unwrap();
      setPendingDeleteMedia(null);
      await refetchMedia();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Anhang gelöscht",
          data: { successText: "Der Anhang wurde dauerhaft entfernt." },
        }),
      );
    } catch (error) {
      setPendingDeleteMedia(null);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Löschen fehlgeschlagen",
          data: {
            errorText: error?.data?.message || "Bitte erneut versuchen.",
          },
        }),
      );
    }
  };

  const handleDeleteMediaCancel = () => {
    setDeletePopoverAnchor(null);
    setPendingDeleteMedia(null);
  };

  if (!event && isFetching) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const begin = event?.eventDateBegin
    ? dayjs(event.eventDateBegin).format("DD.MM.YYYY HH:mm")
    : "–";
  const end = event?.eventDateEnd
    ? dayjs(event.eventDateEnd).format("DD.MM.YYYY HH:mm")
    : null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>{event?.eventName || "Veranstaltung"}</DialogTitle>
        <DialogContent dividers>
          {/* Haupt-Navigation: nur für Booking-Events mit Anmeldungen-Tab */}
          {event?.registrationType === "booking" && canManage && (
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
                <Tab label="Details" />
                <Tab
                  label="Anmeldungen"
                  icon={<Icon icon="mdi:clipboard-list-outline" width={16} />}
                  iconPosition="start"
                />
                <Tab
                  label="Teilnehmerübersicht"
                  icon={<Icon icon="mdi:view-list-outline" width={16} />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>
          )}
          {/* Tabs für Booking-Events */}
          {event?.registrationType === "booking" &&
          canManage &&
          mainTab === 1 ? (
            <EventRegistrationsTab event={event} />
          ) : event?.registrationType === "booking" &&
            canManage &&
            mainTab === 2 ? (
            <EventParticipantsOverviewTab event={event} />
          ) : (
            <Stack spacing={2}>
              {/* Info */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <InfoLine
                    label="Status"
                    value={STATUS_LABELS[event?.status] || event?.status}
                  />
                  <InfoLine label="Beginn" value={begin} />
                  {end && <InfoLine label="Ende" value={end} />}
                  <InfoLine label="Ort" value={event?.locationName} />
                  {event?.street && (
                    <InfoLine label="Straße" value={event.street} />
                  )}
                  {(event?.zip || event?.city) && (
                    <InfoLine
                      label="PLZ / Stadt"
                      value={`${event.zip || ""} ${event.city || ""}`.trim()}
                    />
                  )}
                  {event?.country && (
                    <InfoLine label="Land" value={event.country} />
                  )}
                  <InfoLine
                    label="Kostenpflichtig"
                    value={event?.isFeeBased ? "Ja" : "Nein"}
                  />
                  {event?.isFeeBased && (
                    <InfoLine
                      label="Preis pro Anmeldung"
                      value={`${Number(event?.feeAmount || 0).toFixed(2)} EUR`}
                    />
                  )}
                  {event?.maxParticipants && (
                    <InfoLine
                      label="Max. Teilnehmer"
                      value={String(event.maxParticipants)}
                    />
                  )}
                </Stack>

                {event?.description && (
                  <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Beschreibung
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {event.description}
                    </Typography>
                  </Paper>
                )}
              </Stack>

              {event?.tags?.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {event.tags.map((t, i) => (
                    <Chip key={i} label={t} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}

              <Divider />

              {/* Participants summary */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  background:
                    "linear-gradient(180deg, rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0.01) 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="subtitle1">
                      Anmeldungen & Bildfreigabe
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Registrierungen aus öffentlichen Event-Anmeldungen.
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip
                      size="small"
                      color="success"
                      label={`Bildfreigabe: ${participantsWithConsent.length}`}
                    />
                    <Chip
                      size="small"
                      color="error"
                      label={`Keine Freigabe: ${participantsWithoutConsent.length}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Gesamt: ${registeredParticipants.length}`}
                    />
                  </Stack>
                </Stack>

                <Tabs
                  value={consentTab}
                  onChange={(_, nextTab) => setConsentTab(nextTab)}
                >
                  <Tab
                    label={`Bildfreigabe (${participantsWithConsent.length})`}
                  />
                  <Tab
                    label={`Keine Bildfreigabe (${participantsWithoutConsent.length})`}
                  />
                </Tabs>

                <Box sx={{ mt: 1.5 }}>
                  {activeConsentList.length > 0 ? (
                    <Stack spacing={1}>
                      {activeConsentList.map((entry) => (
                        <Paper
                          key={entry.key}
                          variant="outlined"
                          sx={{ p: 1.25 }}
                        >
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {toMemberName(entry.member)}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {entry?.member?.memberNumber && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Nr. ${entry.member.memberNumber}`}
                                />
                              )}
                              {entry?.member?.degree != null && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={gradLabel(entry.member.degree)}
                                />
                              )}
                              <Chip
                                size="small"
                                color={entry.photoOptOut ? "error" : "success"}
                                label={
                                  entry.photoOptOut
                                    ? "Keine Bildfreigabe"
                                    : "Bildfreigabe"
                                }
                              />
                            </Stack>
                            {entry.createdAt && (
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                Angemeldet am{" "}
                                {dayjs(entry.createdAt).format(
                                  "DD.MM.YYYY HH:mm",
                                )}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity={consentTab === 0 ? "info" : "warning"}>
                      {consentTab === 0
                        ? "Keine Teilnehmer mit Bildfreigabe."
                        : "Keine Teilnehmer ohne Bildfreigabe."}
                    </Alert>
                  )}
                </Box>

                {attendeeOnly.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Weitere Teilnehmer aus Teilnehmerliste (
                      {attendeeOnly.length})
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {attendeeOnly.map((member) => {
                        const id = toMemberId(member);
                        const label = toMemberName(member);
                        return (
                          <Chip
                            key={id}
                            size="small"
                            label={
                              member?.degree != null
                                ? `${label} · ${gradLabel(member.degree)}`
                                : label
                            }
                          />
                        );
                      })}
                    </Stack>
                  </>
                )}
              </Paper>

              <Divider />

              {/* Media */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1">Anhänge</Typography>
                {canManage && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    — Dokumente und Bilder hochladen, Sichtbarkeit steuern oder entfernen
                  </Typography>
                )}
              </Stack>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {canManage && (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<Icon icon="mdi:file-plus" width={18} />}
                      >
                        Dateien auswählen
                        <input
                          hidden
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,image/*,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setSelectedFiles(files);
                          }}
                        />
                      </Button>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showMediaForMembers}
                            onChange={(e) =>
                              setShowMediaForMembers(e.target.checked)
                            }
                          />
                        }
                        label="Für Mitglieder freigeben (bei öffentlichen Events auch für öffentliche Nutzer)"
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleMediaUpload}
                        disabled={
                          isUploadingMedia || selectedFiles.length === 0
                        }
                        startIcon={<Icon icon="mdi:upload" width={18} />}
                      >
                        Hochladen
                      </Button>
                    </Stack>
                  )}

                  {canManage && selectedFiles.length > 0 && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Ausgewählt:{" "}
                      {selectedFiles.map((file) => file.name).join(", ")}
                    </Typography>
                  )}

                  {isMediaFetching && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Medien werden geladen…
                    </Typography>
                  )}

                  {mediaItems.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Noch keine Anhänge hochgeladen.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {mediaItems.map((media) => (
                        <Paper
                          key={media._id}
                          variant="outlined"
                          sx={{ p: 1.5 }}
                        >
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", md: "center" }}
                          >
                            <Stack spacing={0.5}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {media.fileName || "Datei"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                Hochgeladen:{" "}
                                {dayjs(media.uploadedAt).format(
                                  "DD.MM.YYYY HH:mm",
                                )}
                                {media.uploaderName
                                  ? ` · ${media.uploaderName}`
                                  : ""}
                              </Typography>
                              <Chip
                                size="small"
                                color={
                                  media.isVisibleToMembers
                                    ? "success"
                                    : "default"
                                }
                                label={
                                  media.isVisibleToMembers
                                    ? "Mitglieder: Download aktiv"
                                    : "Mitglieder: nicht sichtbar"
                                }
                                sx={{ width: "fit-content" }}
                              />
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {canManage && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    handleToggleMediaVisibility(media)
                                  }
                                  startIcon={
                                    <Icon
                                      icon={
                                        media.isVisibleToMembers
                                          ? "mdi:eye-off-outline"
                                          : "mdi:eye-outline"
                                      }
                                      width={15}
                                    />
                                  }
                                >
                                  {media.isVisibleToMembers
                                    ? "Verbergen"
                                    : "Freigeben"}
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleDownloadMedia(media)}
                                disabled={isDownloadingMedia}
                                startIcon={
                                  <Icon icon="mdi:download" width={15} />
                                }
                              >
                                Download
                              </Button>
                              {canManage && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={(e) =>
                                    handleDeleteMediaClick(e, media)
                                  }
                                  disabled={isDeletingMedia}
                                  startIcon={
                                    <Icon
                                      icon="mdi:trash-can-outline"
                                      width={15}
                                    />
                                  }
                                >
                                  Löschen
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Paper>

              <Divider />

              {/* Comments */}
              <Typography variant="subtitle1">Kommentare</Typography>

              <Stack spacing={1}>
                {(event?.comments || []).map((c) => {
                  const author =
                    c.createdBy?.firstName || c.createdBy?.lastName
                      ? `${c.createdBy.firstName || ""} ${
                          c.createdBy.lastName || ""
                        }`.trim()
                      : "Unbekannt";
                  return (
                    <Paper key={c._id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Stack>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {c.text}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", mt: 0.5 }}
                          >
                            {author} ·{" "}
                            {dayjs(c.createdAt).format("DD.MM.YYYY HH:mm")}
                          </Typography>
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteComment(c._id)}
                        >
                          <Icon icon="mdi:close" width={16} />
                        </IconButton>
                      </Stack>
                    </Paper>
                  );
                })}

                {(event?.comments || []).length === 0 && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Noch keine Kommentare.
                  </Typography>
                )}
              </Stack>

              {/* Add comment */}
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="Kommentar schreiben…"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  fullWidth
                  multiline
                  maxRows={3}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAddComment}
                  disabled={isAddingComment || !commentText.trim()}
                  sx={{ minWidth: 100 }}
                >
                  Senden
                </Button>
              </Stack>
            </Stack>
          )}{" "}
          {/* Ende mainTab-Conditional */}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation popover */}
      <Popover
        open={Boolean(deletePopoverAnchor)}
        anchorEl={deletePopoverAnchor}
        onClose={handleDeleteMediaCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Anhang wirklich löschen?
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Die Datei wird dauerhaft vom Server entfernt. Diese Aktion kann
              nicht rückgängig gemacht werden.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleDeleteMediaConfirm}
              disabled={isDeletingMedia}
              startIcon={<Icon icon="mdi:trash-can-outline" width={15} />}
            >
              Ja, löschen
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDeleteMediaCancel}
            >
              Abbrechen
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

const InfoLine = ({ label, value }) =>
  value ? (
    <Stack direction="row" spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  ) : null;

EventDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  initialTab: PropTypes.number,
};
